import { api, request, requireAccount, withAccount, devRevealAll, type DisclosureLevel } from "./core";
import type { MemberSignal, Snapshot } from "./snapshot";

export interface Event {
  id: number;
  account_id: number;
  scope: string; // personal | team
  target_id: string;
  repo_id?: number;
  timestamp: string; // YYYY-MM or YYYY-MM-DD
  title: string;
  description: string;
  tags?: string[];
  source: string; // auto | manual
  visibility: string; // private | team | public
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Team Health (from EIS team package)
export interface TeamSnapshot {
  id: number;
  snapshot_id: number;
  domain: string;
  member_count: number;
  core_member_count: number;
  total_member_count: number;
  avg_total: number;
  avg_production: number;
  avg_catalysis: number;
  avg_survival: number;
  avg_design: number;
  avg_breadth: number;
  avg_debt_cleanup: number;
  avg_indispensability: number;
  // Team footprint (sum over core members). Present only on embedded
  // team_health from summary_json — the legacy team_snapshots API row
  // doesn't carry these, so they're optional.
  total_lines_added?: number;
  total_alive_lines?: number;
  health_complementarity: number;
  health_growth_potential: number;
  health_sustainability: number;
  health_debt_balance: number;
  health_productivity_density: number;
  health_catalysis_consistency: number;
  health_risk_ratio: number;
  health_aar: number;
  health_anchor_density: number;
  health_architecture_coverage: number;
  class_character: string;
  class_structure: string;
  class_culture: string;
  class_phase: string;
  class_risk: string;
  role_dist: Record<string, number> | null;
  style_dist: Record<string, number> | null;
  state_dist: Record<string, number> | null;
  warnings: string[] | null;
}

// DomainCoverageDTO mirrors the BE's per-domain repo coverage (analyzed/active).
// A domain rollup is COMPLETE iff analyzed === active && active > 0; otherwise the
// worker observed only some of the domain's active repos, so its NON-LINEAR eis
// Total/axes are deflated. Used to render "観測 N/M リポ".
export interface DomainCoverageDTO {
  analyzed: number;
  active: number;
}

export interface PeriodSignals {
  snapshot: Snapshot;
  signals: MemberSignal[];
  events?: Event[];
  // Historical team_health embedded in observations_snapshots.summary_json.
  // Present when the sha was observed after team_health persistence landed;
  // older observations surface as undefined (UI falls back to the live
  // teamHealth or hides the classification chips).
  team_health?: TeamSnapshot;
  // Populated when the BE flattens an observation into per-repository rows
  // (see flattenLegacyDTOs in snapshot.go). These rows feed StarDetail's
  // "Per-Repository Breakdown" panel only — page-level "current state"
  // views (Constellation / Observatory) must drop them before per-month
  // dedup so per-repo signals don't get promoted to represent the whole
  // month.
  repo_name?: string;
  // When the worker last observed this sha (≠ the commit's author date).
  // Drives the Observatory "last observed / stale" freshness indicator.
  last_observed_at?: string;
  // True when the BE derived this period's domain headline ONLY from
  // known-partial observations (see GalaxyViewDTO.observation_incomplete). The UI
  // must mark the domain Total "観測未完了" rather than render the drifting value
  // as authoritative. Carried straight from the DTO by periodFromGalaxyDTO.
  observation_incomplete?: boolean;
  // Repo coverage (analyzed/active) of the period's winning source, for the
  // "観測 N/M リポ" hint. Absent for legacy/unknown coverage.
  coverage?: DomainCoverageDTO;
}

// timelineApi exposes the timeline TRIGGER only — reads now flow
// through galaxyApi.timeline (canonical, deduped, merged, filtered).
export const timelineApi = {
  observePast: (accountLogin: string, span?: string, periods?: number) =>
    api.post<{ status: string }>("/timeline/observe", {
      account_login: requireAccount(accountLogin, "timelineApi.observePast"),
      span: span || "1m",
      periods: periods ?? 0,
    }),
};

// Galaxy view DTOs — mirror the BE's service.GalaxyViewDTO shape.
// One DTO per (galaxy, domain, period); FE pages don't run their own
// dedup/merge/filter logic anymore.
export interface PeriodSnapshotDTO {
  git_sha: string;
  sha_author_date: string;  // RFC3339
  period_start: string;     // mirrors sha_author_date for FE compat
  period_end: string;       // mirrors period_start for single-sha periods
  repo_set_hash: string;
  last_observed_at: string; // RFC3339 — when the worker last observed (≠ commit date)
}

export interface MemberSignalDTO {
  author: string;
  github_user_id: number;
  github_login?: string;
  production: number;
  catalysis: number;
  survival: number;
  // Survival decomposed: robust = blame in high-change-pressure
  // modules, dormant = blame in low-pressure modules. Impact splits
  // Survival 80/20 into these.
  robust_survival: number;
  dormant_survival: number;
  design: number;
  breadth: number;
  debt_cleanup: number;
  indispensability: number;
  gravity: number;
  // 通期/all-time twin of `gravity` (the lasting raw-survival footprint, redacted
  // alongside `gravity`). The BE sends it (dto.go MemberSignalDTO); the FE must
  // carry it through signalFromDTO or the Constellation's 生涯重力 metric reads
  // undefined for everyone. Optional: 0/absent on observations taken before
  // lifetime_gravity existed, and on redacted members.
  lifetime_gravity?: number;
  // Real gravity that survives D-05 redaction (the 世代交代 handover field). The
  // BE always sends it; the FE must carry it through signalFromDTO or the
  // generational views fall back to the redacted `gravity` (zeroed → all-0 cells).
  gravity_handover?: number;
  total: number;
  role_archetype: string;
  style_archetype: string;
  state_archetype: string;
  recently_active: boolean;
  // D-05 disclosure level applied to this row for the current viewer
  // (see DisclosureLevel). Drives the galaxy-view member labels.
  disclosure: DisclosureLevel;
  // Structural "load-bearing member" flag — see MemberSignal.is_core. Carries the
  // qualitative core fact without any number; false on anonymised rows.
  is_core?: boolean;
}

export interface TeamHealthDTO {
  class_character: string;
  class_structure: string;
  class_culture: string;
  class_phase: string;
  class_risk: string;
  avg_total: number;
  avg_production: number;
  avg_catalysis: number;
  avg_survival: number;
  avg_design: number;
  avg_breadth: number;
  avg_debt_cleanup: number;
  avg_indispensability: number;
  total_lines_added: number;
  total_alive_lines: number;
  core_member_count: number;
  total_member_count: number;
  role_dist?: Record<string, number>;
}

export interface GalaxyViewDTO {
  galaxy_id: number;
  domain: string;
  period: PeriodSnapshotDTO;
  signals: MemberSignalDTO[];
  team_health?: TeamHealthDTO;
  // True only when the surfaced domain headline was derived ONLY from
  // KNOWN-PARTIAL observations — no complete-coverage rollup ever landed for this
  // (domain, month). The number is then deflated and can drift between partial
  // runs (a repo that persistently fails to clone), so the FE must show
  // "観測未完了 / Observation incomplete" instead of presenting it as final.
  // Absent/false for complete, single-snapshot, and legacy (no-coverage) months.
  observation_incomplete?: boolean;
  // Coverage of the winning source (analyzed/active) when KNOWN; absent for
  // legacy/unknown. For an ALL rollup it is the worst (lowest-ratio) partial
  // constituent so "N/M" matches the incomplete flag.
  coverage?: DomainCoverageDTO;
}

// Accumulated (蓄積) timeline view — mirrors galaxyview.TimelineAccumulation. The
// career-total lens: Σ over the timeline (accum) + max (peak) + mean over active
// periods (avg), per member and for the team, with per-member archetype histograms.
export interface ArchetypeCountDTO {
  name: string;
  count: number;
}
export interface MemberAccumulationDTO {
  github_user_id: number;
  github_login?: string;
  accum_gravity: number;
  accum_signal: number;
  peak_gravity: number;
  peak_signal: number;
  avg_gravity: number;
  avg_signal: number;
  active_periods: number;
  // Σ over the timeline of each raw 7-axis score, keyed by axis JSON name (production,
  // catalysis, survival, design, breadth, debt_cleanup, indispensability). Lets the
  // constellation rank/plot ANY axis by career total, not just gravity/signal.
  axis_accum?: Record<string, number>;
  role_counts?: ArchetypeCountDTO[];
  style_counts?: ArchetypeCountDTO[];
  state_counts?: ArchetypeCountDTO[];
}
export interface TeamAccumulationDTO {
  accum_gravity: number;
  accum_signal: number;
  avg_gravity: number;
  avg_signal: number;
  periods: number;
}
export interface TimelineAccumulationDTO {
  team: TeamAccumulationDTO;
  members: MemberAccumulationDTO[];
}

export interface PerRepoEntryDTO {
  repo_name: string;
  domain: string;
  signals: MemberSignalDTO[];
}

export interface PerRepoViewDTO {
  galaxy_id: number;
  domain: string;
  period: PeriodSnapshotDTO;
  repos: PerRepoEntryDTO[];
}

// galaxyApi is the canonical read surface for Observatory /
// Constellation / StarDetail / Logbook. Each endpoint takes the
// (account, domain, month) coordinates; the BE owns the dedup,
// canonical merge, and disclosure filter pipeline.
//
// Domain is a string; "" means cross-domain rollup (ALL).
// Month is YYYY-MM; "" means latest available for the domain.
//
// `accountLogin` is required and must be derived from useParams().account.
// requireAccount() throws on empty so wrong-galaxy reads can't sneak in.
// `team` scopes the view to one department/team (teams.id); omitted/0 = the
// whole galaxy. The BE resolves the id to the team's member set and filters the
// aggregate to it (galaxy-bounded — a cross-galaxy id resolves to no members).
export const galaxyApi = {
  view: (accountLogin: string, domain: string, month?: string, team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.view"));
    if (domain) p.set("domain", domain);
    if (month) p.set("month", month);
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<GalaxyViewDTO | { period: null; signals: [] }>(`/galaxy/view?${p}`);
  },
  // viewMany requests SEVERAL domains' headlines in one read (""=ALL rollup), so
  // the Observatory / StarDetail headline pays the per-request setup (snapshot
  // fetch + parse + disclosure + mask roster) once instead of once per domain
  // tab. Returns {views:{domain:DTO|null}} keyed by the requested domain; a
  // domain with no data is null.
  viewMany: (accountLogin: string, domains: string[], month?: string, team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.viewMany"));
    p.set("domains", domains.join(","));
    if (month) p.set("month", month);
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<{ views: Record<string, GalaxyViewDTO | null> }>(`/galaxy/view?${p}`);
  },
  timeline: (accountLogin: string, domain: string, team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.timeline"));
    if (domain) p.set("domain", domain);
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<GalaxyViewDTO[]>(`/galaxy/timeline?${p}`);
  },
  // accumulated folds the timeline into the 蓄積/最大/平均 lenses (team + per member)
  // — the career-total view the observatory / StarDetail / constellation show in 通期.
  accumulated: (accountLogin: string, domain: string, team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.accumulated"));
    if (domain) p.set("domain", domain);
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<TimelineAccumulationDTO>(`/galaxy/accumulated?${p}`);
  },
  // accumulatedMany requests SEVERAL domains' 通期 accumulation in one read (""=ALL
  // rollup), the accumulation twin of timelineMany. Each domain folds from the SAME
  // warm timeline cache slot on the BE, so a page showing the ALL rollup + every
  // sub-domain pays one request instead of one per tab. Returns
  // {accumulated:{domain:TimelineAccumulationDTO}} keyed by the requested domain.
  accumulatedMany: (accountLogin: string, domains: string[], team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.accumulatedMany"));
    p.set("domains", domains.join(","));
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<{ accumulated: Record<string, TimelineAccumulationDTO> }>(`/galaxy/accumulated?${p}`);
  },
  // timelineMany requests SEVERAL domains' timelines in one read (""=ALL rollup),
  // the trajectory twin of viewMany. Per-domain cache slots are preserved on the
  // BE, so a single-domain timeline() and this share the same cache. Returns
  // {timelines:{domain:DTO[]}} keyed by the requested domain.
  timelineMany: (accountLogin: string, domains: string[], team?: number) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.timelineMany"));
    p.set("domains", domains.join(","));
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<{ timelines: Record<string, GalaxyViewDTO[]> }>(`/galaxy/timeline?${p}`);
  },
  // domain selects ONE domain; domains (preferred for multi-tab pages) requests
  // SEVERAL in one read so the BE pays the per-request setup once instead of per
  // domain. Pass one or the other.
  perRepo: (accountLogin: string, domain: string, month?: string, team?: number, domains?: string[]) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "galaxyApi.perRepo"));
    if (domains && domains.length > 0) p.set("domains", domains.join(","));
    else if (domain) p.set("domain", domain);
    if (month) p.set("month", month);
    if (team) p.set("team", String(team));
    if (devRevealAll()) p.set("dev_reveal_all", "true");
    return api.get<PerRepoViewDTO | { period: null; repos: [] }>(`/galaxy/per-repo?${p}`);
  },
};

export const teamHealthApi = {
  latest: (accountLogin: string) =>
    api.get<TeamSnapshot[]>(withAccount("/team-health/latest", requireAccount(accountLogin, "teamHealthApi.latest"))),
};

export const eventApi = {
  list: (accountLogin: string, targetId?: string, scope?: string) => {
    const extra: Record<string, string> = {};
    if (targetId) extra.target_id = targetId;
    if (scope) extra.scope = scope;
    return api.get<Event[]>(withAccount("/events", requireAccount(accountLogin, "eventApi.list"), extra));
  },
  // listAll returns every event in the galaxy (all members) — the chronicle lane
  // surfaces manually-written ones alongside notes/ADRs.
  listAll: (accountLogin: string) =>
    api.get<Event[]>(withAccount("/events", requireAccount(accountLogin, "eventApi.listAll"), { all: "true" })),
  create: (accountLogin: string, data: { scope?: string; target_id: string; timestamp: string; title: string; description?: string; tags?: string[]; visibility?: string }) =>
    api.post<Event>(withAccount("/events", requireAccount(accountLogin, "eventApi.create")), data),
  update: (accountLogin: string, data: { id: number; title: string; description?: string; tags?: string[]; scope?: string; visibility?: string }) =>
    api.put<Event>(withAccount("/events", requireAccount(accountLogin, "eventApi.update")), data),
  delete: (accountLogin: string, id: number) =>
    request<void>(withAccount("/events", requireAccount(accountLogin, "eventApi.delete")), { method: "DELETE", body: JSON.stringify({ id }) }),
};
